import { useState } from 'react';
import type { CSSProperties, FC, MouseEvent, ReactElement } from 'react';
import clsx from 'clsx';
import { Modal } from '@ui';
import { observer } from 'mobx-react';
import useCommentsLayer from '@customHooks/useCommentsLayer';
import useWindowSize from '@customHooks/useWindowSize';
import { notifications } from '@utils/notifications';
import type { storeAddressT } from '@store/UserStore.type';
import { useStores } from '@root/src/stores';
import OrderRegisterCustomMap from '@components/OrderRegistration/components/OrderRegisterCustomMap';
import './index.scss';

import type { DeliveryAddressModalProps } from './types';
import useDeliveryModalStage from './hooks/useDeliveryModalStage';
import useListScrollIntoView from '@customHooks/useListScrollIntoView';
import AddressListStage from './stages/AddressListStage';
import AddressFormStage from './stages/AddressFormStage';
import UserDataStage from './stages/UserDataStage';

export type { SaveAddressData, InitialState, ModalState } from './types';

const DeliveryAddressModal: FC<DeliveryAddressModalProps> = observer(
  ({
    initialState,
    defState,
    isMobileMapOpened,
    coords,
    selectedAddress,
    mapRef,
    editAddress,
    iconContentTemplate,
    isMainAddress,
    dadataId,
    address,
    fio,
    phone,
    comment,
    flat,
    setFields,
    setShowModal,
    selectAddress,
    removeAddress,
    onMapClick,
    onLoadMap,
    isDefaultMark,
    saveAddress,
    addressComponent,
    userComponent,
    prepareNewAddress,
    cancelChanges,
    onEditAddress,
    selectMainAddress
  }) => {
    const { userStore, cartStore } = useStores();
    const { moscowAddresses } = userStore;
    const { cart } = cartStore;
    const { allCommentsOptions, changeNewParams, resetComments } =
      useCommentsLayer({ selectedAddress, editAddress });

    const { isMobile, viewportHeight } = useWindowSize();

    const peopleAddressId = cart?.peopleAddress?.id ?? null;

    const {
      modalStage,
      setModalStage,
      isShowComment,
      toggleShowComment,
      startNewAddress
    } = useDeliveryModalStage({
      initialState,
      defState,
      prepareNewAddress
    });

    // Editable fields are owned by the parent's useAddressForm; write through setFields.
    const setFlat = (v: string): void => {
      setFields({ flat: v });
    };
    const setComment = (v: string): void => {
      setFields({ comment: v });
    };
    const setFio = (v: string): void => {
      setFields({ fio: v });
    };
    const setPhone = (v: string): void => {
      setFields({ phone: v });
    };

    const { elRefs } = useListScrollIntoView({
      itemCount: moscowAddresses.length,
      activeKey: selectedAddress ?? peopleAddressId
    });

    const [isOpenMap, setIsOpenMap] = useState(false);

    const culcHeight = isOpenMap ? -20 : 0;
    const mobileListStyle = {
      height: `${viewportHeight - 148 - 48 - culcHeight}px`
    };
    const mobileFormMapHeight = `${
      viewportHeight - 148 - 48 - 60 - culcHeight
    }px`;
    const mobileFormWrapperStyle: CSSProperties = {
      height: `${viewportHeight - 145}px`
    };

    const closeModal = (): void => {
      setShowModal(false);
    };

    const changeStateModalNew = (): void => {
      resetComments();
      startNewAddress();
    };

    const changeStateModalEdit = (
      addressItem: storeAddressT,
      e: MouseEvent<HTMLImageElement>
    ): void => {
      setModalStage('editAddress');
      onEditAddress(addressItem, e);
    };

    const changeStateModalNext = (): void => {
      if (dadataId) {
        setModalStage(
          modalStage === 'editAddress' ? 'userDataEdit' : 'userDataNew'
        );
      } else {
        notifications('Проверьте правильность адреса доставки!', 'Внимание');
      }
    };

    const changeStateModalBack = (): void => {
      setModalStage(
        modalStage === 'userDataEdit' ? 'editAddress' : 'addAddress'
      );
    };

    const changeStateModalInit = (): void => {
      cancelChanges();
      setModalStage('list');
      resetComments();
    };

    const deleteAddress = (
      e: MouseEvent<HTMLImageElement>,
      item: storeAddressT
    ): void => {
      e.stopPropagation();
      removeAddress(item, e);
    };

    const saveDataWithParams = (): void => {
      saveAddress(
        {
          fio,
          phone,
          comment,
          flat,
          primary: isMainAddress,
          coords: null,
          confirmedByUser: true,
          dadata: dadataId
        },
        {
          orderOptions: allCommentsOptions.orderOptions ?? [],
          checkboxes: allCommentsOptions.checkboxes ?? [],
          selects: allCommentsOptions.selects ?? [],
          orderChange: allCommentsOptions.orderChange ?? {},
          orderHome: allCommentsOptions.orderHome ?? {}
        }
      ).then(() => {
        closeModal();
      });
    };

    const renderStage = (): ReactElement => {
      if (modalStage === 'list') {
        return (
          <AddressListStage
            isMobile={isMobile}
            isOpenMap={isOpenMap}
            setIsOpenMap={setIsOpenMap}
            selectedAddress={selectedAddress}
            elRefs={elRefs}
            mobileListStyle={mobileListStyle}
            mapRef={mapRef}
            coords={coords}
            editAddress={editAddress}
            iconContentTemplate={iconContentTemplate}
            onMapClick={onMapClick}
            onLoadMap={onLoadMap}
            isDefaultMark={isDefaultMark}
            selectAddress={selectAddress}
            changeStateModalEdit={changeStateModalEdit}
            deleteAddress={deleteAddress}
            onAddNewAddress={changeStateModalNew}
            onConfirm={closeModal}
            onClose={closeModal}
          />
        );
      }

      if (modalStage === 'addAddress' || modalStage === 'editAddress') {
        return (
          <AddressFormStage
            isMobile={isMobile}
            modalStage={modalStage}
            mobileFormWrapperStyle={mobileFormWrapperStyle}
            mobileFormMapHeight={mobileFormMapHeight}
            isOpenMap={isOpenMap}
            setIsOpenMap={setIsOpenMap}
            mapRef={mapRef}
            coords={coords}
            editAddress={editAddress}
            iconContentTemplate={iconContentTemplate}
            isDefaultMark={isDefaultMark}
            selectedAddress={selectedAddress}
            selectAddress={selectAddress}
            onMapClick={onMapClick}
            onLoadMap={onLoadMap}
            address={address}
            flat={flat}
            setFlat={setFlat}
            fio={fio}
            setFio={setFio}
            phone={phone}
            setPhone={setPhone}
            comment={comment}
            setComment={setComment}
            isShowComment={isShowComment}
            toggleShowComment={toggleShowComment}
            isMainAddress={isMainAddress}
            selectMainAddress={selectMainAddress}
            allCommentsOptions={allCommentsOptions}
            changeNewParams={changeNewParams}
            addressComponent={addressComponent}
            userComponent={userComponent}
            onClose={closeModal}
            onCancel={changeStateModalInit}
            onNext={changeStateModalNext}
            onSave={saveDataWithParams}
          />
        );
      }

      return (
        <UserDataStage
          modalStage={modalStage}
          fio={fio}
          setFio={setFio}
          phone={phone}
          setPhone={setPhone}
          userComponent={userComponent}
          onBack={changeStateModalBack}
          onSave={saveDataWithParams}
        />
      );
    };

    return (
      <Modal
        className='modal-with-route modal-pickup'
        withoutCross
        placementMobile='fullscreen'
        onClose={closeModal}
      >
        <div className='modal-choose-address'>
          <div className='left-part'>{renderStage()}</div>
          {!isMobile && (
            <div
              className={clsx(
                'right-map',
                isMobileMapOpened && 'right-map--mobile-map-opened'
              )}
            >
              <OrderRegisterCustomMap
                mapRef={mapRef}
                onMapClick={onMapClick}
                onLoadMap={onLoadMap}
                coords={coords}
                editAddress={editAddress}
                iconContentTemplate={iconContentTemplate}
                isShowMapMarkPlace={
                  (modalStage === 'addAddress' && !!address) ||
                  modalStage === 'editAddress'
                }
                isDefaultMark={isDefaultMark}
                selectedAddress={selectedAddress}
                selectAddress={selectAddress}
                width='532px'
                height='585px'
                mapRefName='mapRefModal'
                isShowClusterer
              />
            </div>
          )}
        </div>
      </Modal>
    );
  }
);

export default DeliveryAddressModal;
